import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store';
import { resetAgree, showCollect, showUse } from '../common/popup/popupSlice';
import { postRecruit } from './contactSlice';
import {
  AgreeLabel,
  AgreeList,
  AgreeRow,
  Chip,
  ChipRow,
  FieldGrid,
  FieldLabel,
  FieldTitle,
  FileButton,
  FileHelp,
  FileMessage,
  FormRoot,
  HelpText,
  HiddenFileInput,
  InputLabel,
  Required,
  SubmitButton,
  SubmitMessage,
  SubmitRow,
  TextArea,
  TextField,
  ViewButton,
} from './contactFormStyles';

const FIELDS = [
  { code: 'C1901', label: 'Front End' },
  { code: 'C1902', label: 'Back End' },
  { code: 'C1903', label: 'Mobile[Android]' },
  { code: 'C1904', label: 'Mobile[iOS]' },
  { code: 'C1905', label: '기타' },
];

const ALLOWED_EXT = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'pdf', 'png', 'jpg', 'jpeg', 'zip'];
const MAX_SIZE = 20 * 1024 * 1024;

const EMAIL_REGEX = /^[\w!#$%&'*+/=?`{|}~^-]+(?:\.[\w!#$%&'*+/=?`{|}~^-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,6}$/;

const INITIAL_FIELD = {
  isSelected: '',
  nameVal: '',
  numberVal: '',
  emailVal: '',
  urlVal: '',
  fileVal: null,
  textVal: '',
  agrUse: '',
  agrCollect: '',
};

// 입사지원 — POST /api/contact/join (담당자명 없이 inqerNm 하나만 사용합니다)
export default function RecruitContact() {
  const dispatch = useAppDispatch();
  const popup = useSelector(state => state.popup);
  const fileInputRef = useRef(null);

  const [field, setField] = useState(INITIAL_FIELD);
  const [phoneNum, setPhoneNum] = useState('');
  const [fileMessage, setFileMessage] = useState('');
  const [submitMessage, setSubmitMessage] = useState('');
  const [disabledSubmit, setDisabledSubmit] = useState(false);

  useEffect(() => {
    setField(prev => ({ ...prev, agrUse: popup.useContent, agrCollect: popup.collectContent }));
  }, [popup]);

  const onSelectField = code => {
    setField(prev => ({ ...prev, isSelected: code }));
    setSubmitMessage('');
  };

  const onChangeName = e => setField(prev => ({ ...prev, nameVal: e.target.value }));
  const onChangeEmail = e => setField(prev => ({ ...prev, emailVal: e.target.value }));
  const onChangeUrl = e => setField(prev => ({ ...prev, urlVal: e.target.value }));
  const onChangeText = e => setField(prev => ({ ...prev, textVal: e.target.value }));

  const onChangeNumber = e => {
    const val = e.target.value.substr(0, 13);

    setPhoneNum(
      val
        .replace(/[^0-9]/g, '')
        .replace(/^(\d{2,3})(\d{3,4})(\d{4})$/, '$1-$2-$3')
        .replace(/(-{1,2})$/g, '')
    );
    setField(prev => ({ ...prev, numberVal: val.replace(/[^0-9]/g, '') }));
  };

  const fileUpload = e => {
    const file = e.target.files && e.target.files[0];

    if (!file) {
      setField(prev => ({ ...prev, fileVal: null }));
      setFileMessage('');
      return;
    }

    if (file.size > MAX_SIZE) {
      e.target.value = '';
      setField(prev => ({ ...prev, fileVal: null }));
      setFileMessage('파일 크기는 20MB 이하여야 합니다.');
      return;
    }

    const ext = file.name.split('.').pop().toLowerCase();
    if (ALLOWED_EXT.indexOf(ext) === -1) {
      e.target.value = '';
      setField(prev => ({ ...prev, fileVal: null }));
      setFileMessage('업로드 가능한 확장자는 doc, docx, xls, xlsx, ppt, pptx, pdf, png, jpg, jpeg, zip 입니다.');
      return;
    }

    setField(prev => ({ ...prev, fileVal: file }));
    setFileMessage(`${file.name} 첨부됨`);
  };

  const onClickUsePopup = () => dispatch(showUse());
  const onClickCollectPopup = () => dispatch(showCollect());

  const validate = () => {
    const { isSelected, nameVal, numberVal, emailVal, fileVal, agrUse, agrCollect } = field;

    if (!isSelected) return '지원 분야를 선택해주세요.';
    if (nameVal.trim() === '') return '이름을 입력해주세요.';
    if (numberVal === '') return '연락처를 입력해주세요.';
    if (numberVal.length < 9 || numberVal.length > 11) return '연락처 형식이 올바르지 않습니다.';
    if (emailVal === '') return '이메일을 입력해주세요.';
    if (!EMAIL_REGEX.test(emailVal)) return '이메일 형식이 올바르지 않습니다.';
    if (!fileVal) return '이력서 파일을 업로드해주세요.';
    if (agrUse === '' || agrCollect === '') return '개인정보 관련 동의를 모두 확인해주세요.';

    return '';
  };

  const onSubmit = e => {
    e.preventDefault();

    const error = validate();
    if (error) {
      setSubmitMessage(error);
      return;
    }

    const formData = {
      joinSupFidCd: field.isSelected,
      inqerNm: field.nameVal,
      inqerPhoneNo: field.numberVal,
      inqerEmailAddr: field.emailVal,
      ptfUrlAddr: field.urlVal,
      file: field.fileVal,
      inqCont: field.textVal,
      prsInfo3cProAgrCont: field.agrUse,
      prsInfoCltAgrCont: field.agrCollect,
    };

    setDisabledSubmit(true);
    setSubmitMessage('전송 중…');

    Promise.resolve(dispatch(postRecruit(formData))).then(resolved => {
      setDisabledSubmit(false);

      if (resolved.type.endsWith('fulfilled')) {
        setField(INITIAL_FIELD);
        setPhoneNum('');
        setFileMessage('');
        if (fileInputRef.current) fileInputRef.current.value = '';
        dispatch(resetAgree());
        setSubmitMessage('입사지원이 완료되었습니다. 담당자가 확인 후 연락드리겠습니다.');
      } else {
        setSubmitMessage('오류가 발생하여 정상적으로 접수되지 않았습니다. info@br-it.co.kr 로 보내주세요.');
      }
    });
  };

  return (
    <FormRoot onSubmit={onSubmit} noValidate>
      <div>
        <FieldTitle>
          <Required>*</Required> 1. 지원하실 분야 1가지를 선택해주세요.
        </FieldTitle>
        <ChipRow>
          {FIELDS.map(item => (
            <Chip
              key={item.code}
              type="button"
              aria-pressed={field.isSelected === item.code}
              $on={field.isSelected === item.code}
              onClick={() => onSelectField(item.code)}
            >
              {item.label}
            </Chip>
          ))}
        </ChipRow>
      </div>

      <div>
        <FieldLabel htmlFor="recruitText">2. 자기소개와 지원 동기를 작성해주세요.</FieldLabel>
        <HelpText>경력 사항, 주요 프로젝트, 사용 기술 스택 등을 자유롭게 작성해주세요.</HelpText>
        <TextArea id="recruitText" name="message" rows="6" spellCheck="false" value={field.textVal} onChange={onChangeText} />
      </div>

      <FieldGrid>
        <div>
          <InputLabel htmlFor="recruitUrl">포트폴리오 URL</InputLabel>
          <TextField
            id="recruitUrl"
            type="text"
            placeholder="포트폴리오 주소가 있다면 입력해주세요."
            value={field.urlVal}
            onChange={onChangeUrl}
          />
        </div>
        <div>
          <InputLabel htmlFor="recruitName">
            <Required>*</Required> 이름
          </InputLabel>
          <TextField
            id="recruitName"
            type="text"
            placeholder="지원자 이름을 입력해주세요."
            value={field.nameVal}
            onChange={onChangeName}
          />
        </div>
        <div>
          <InputLabel htmlFor="recruitTel">
            <Required>*</Required> 연락처
          </InputLabel>
          <TextField
            id="recruitTel"
            type="tel"
            placeholder="연락 가능한 연락처를 입력해주세요."
            value={phoneNum}
            onChange={onChangeNumber}
          />
        </div>
        <div>
          <InputLabel htmlFor="recruitEmail">
            <Required>*</Required> 이메일
          </InputLabel>
          <TextField
            id="recruitEmail"
            type="text"
            placeholder="이메일 주소를 입력해주세요."
            value={field.emailVal}
            onChange={onChangeEmail}
          />
        </div>
      </FieldGrid>

      <div>
        <FileButton htmlFor="recruitFile">파일 업로드</FileButton>
        <HiddenFileInput ref={fileInputRef} id="recruitFile" name="file" type="file" onChange={fileUpload} />
        <FileHelp>
          (필수) 이력서 및 포트폴리오를 첨부해 주세요. 최대 20MB / 2개 이상은 zip으로 압축해 올려주세요.
        </FileHelp>
        {fileMessage && <FileMessage>{fileMessage}</FileMessage>}
      </div>

      <AgreeList>
        <AgreeRow>
          <AgreeLabel htmlFor="check1">
            <input id="check1" type="checkbox" name="agree" checked={popup.useAgree} onClick={onClickUsePopup} readOnly />
            <span>
              개인정보 수집 · 이용 동의 <Required>(필수)</Required>
            </span>
          </AgreeLabel>
          <ViewButton type="button" onClick={onClickUsePopup}>
            내용 보기
          </ViewButton>
        </AgreeRow>
        <AgreeRow>
          <AgreeLabel htmlFor="check2">
            <input
              id="check2"
              type="checkbox"
              name="agree"
              checked={popup.collectAgree}
              onClick={onClickCollectPopup}
              readOnly
            />
            <span>
              개인정보 취급위탁에 대한 동의 <Required>(필수)</Required>
            </span>
          </AgreeLabel>
          <ViewButton type="button" onClick={onClickCollectPopup}>
            내용 보기
          </ViewButton>
        </AgreeRow>
      </AgreeList>

      <SubmitRow>
        {submitMessage && <SubmitMessage role="status">{submitMessage}</SubmitMessage>}
        <SubmitButton type="submit" disabled={disabledSubmit}>
          &lt;&#47;Send&gt;
        </SubmitButton>
      </SubmitRow>
    </FormRoot>
  );
}
